/**
 * Voice Note Recording
 *
 * Handles recording voice notes with the MediaRecorder API.
 */

const MAX_RECORDING_DURATION = 30 * 1000 // 30 seconds in ms
const AUDIO_MIME_TYPE = 'audio/webm;codecs=opus'
const FALLBACK_MIME_TYPE = 'audio/webm'

export interface RecordingOptions {
  maxDuration?: number
  onDataAvailable?: (blob: Blob) => void
  onTimeUpdate?: (elapsedMs: number) => void
  onError?: (error: Error) => void
}

export interface RecordingResult {
  blob: Blob
  duration: number
  mimeType: string
}

let mediaRecorder: MediaRecorder | null = null
let recordingStream: MediaStream | null = null
let recordingStartTime: number = 0
let recordingTimer: ReturnType<typeof setInterval> | null = null
let maxDurationTimer: ReturnType<typeof setTimeout> | null = null
let audioChunks: Blob[] = []

/**
 * Request microphone permission
 */
export async function requestMicrophonePermission(): Promise<boolean> {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
    // Stop immediately - we just wanted to check permission
    stream.getTracks().forEach((track) => track.stop())
    return true
  } catch {
    return false
  }
}

/**
 * Check if microphone is available
 */
export async function isMicrophoneAvailable(): Promise<boolean> {
  try {
    const devices = await navigator.mediaDevices.enumerateDevices()
    return devices.some((device) => device.kind === 'audioinput')
  } catch {
    return false
  }
}

/**
 * Get the best supported audio MIME type
 */
export function getSupportedMimeType(): string {
  if (MediaRecorder.isTypeSupported(AUDIO_MIME_TYPE)) {
    return AUDIO_MIME_TYPE
  }
  if (MediaRecorder.isTypeSupported(FALLBACK_MIME_TYPE)) {
    return FALLBACK_MIME_TYPE
  }
  // Let the browser pick
  return ''
}

/**
 * Start recording audio
 */
export async function startRecording(
  options: RecordingOptions = {}
): Promise<void> {
  if (mediaRecorder && mediaRecorder.state === 'recording') {
    throw new Error('Recording already in progress')
  }

  const {
    maxDuration = MAX_RECORDING_DURATION,
    onTimeUpdate,
    onError,
  } = options

  try {
    // Get audio stream
    recordingStream = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      },
    })

    // Create recorder
    const mimeType = getSupportedMimeType()
    mediaRecorder = new MediaRecorder(recordingStream, {
      mimeType: mimeType || undefined,
    })

    audioChunks = []

    mediaRecorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        audioChunks.push(event.data)
      }
    }

    mediaRecorder.onerror = (event) => {
      console.error('[Recorder] Error:', event)
      onError?.(new Error('Recording failed'))
      cleanupRecording()
    }

    // Start recording
    mediaRecorder.start(100) // Collect data every 100ms
    recordingStartTime = Date.now()

    // Time update interval
    if (onTimeUpdate) {
      recordingTimer = setInterval(() => {
        const elapsed = Date.now() - recordingStartTime
        onTimeUpdate(elapsed)
      }, 100)
    }

    // Max duration timeout
    maxDurationTimer = setTimeout(() => {
      stopRecording()
    }, maxDuration)

    console.log('[Recorder] Started recording')
  } catch (error) {
    console.error('[Recorder] Failed to start:', error)
    cleanupRecording()
    throw error
  }
}

/**
 * Stop recording and return the audio blob
 */
export async function stopRecording(): Promise<RecordingResult | null> {
  if (!mediaRecorder || mediaRecorder.state !== 'recording') {
    console.warn('[Recorder] No recording in progress')
    return null
  }

  return new Promise((resolve) => {
    mediaRecorder!.onstop = () => {
      const duration = (Date.now() - recordingStartTime) / 1000
      const mimeType = mediaRecorder!.mimeType || FALLBACK_MIME_TYPE
      const blob = new Blob(audioChunks, { type: mimeType })

      console.log('[Recorder] Stopped recording, duration:', duration, 'size:', blob.size)

      cleanupRecording()

      resolve({
        blob,
        duration,
        mimeType,
      })
    }

    mediaRecorder!.stop()
  })
}

/**
 * Cancel recording without returning data
 */
export function cancelRecording(): void {
  cleanupRecording()
  console.log('[Recorder] Recording cancelled')
}

/**
 * Check if currently recording
 */
export function isRecording(): boolean {
  return mediaRecorder?.state === 'recording'
}

/**
 * Get current recording duration in ms
 */
export function getRecordingDuration(): number {
  if (!isRecording()) return 0
  return Date.now() - recordingStartTime
}

/**
 * Clean up recording resources
 */
function cleanupRecording(): void {
  if (recordingTimer) {
    clearInterval(recordingTimer)
    recordingTimer = null
  }

  if (maxDurationTimer) {
    clearTimeout(maxDurationTimer)
    maxDurationTimer = null
  }

  if (recordingStream) {
    recordingStream.getTracks().forEach((track) => track.stop())
    recordingStream = null
  }

  mediaRecorder = null
  audioChunks = []
}
